import { AnchorButton } from '@payaca/ambrosia';
import { FC, useEffect, useMemo, useState } from 'react';
import { Link, useHistory, useRouteMatch } from 'react-router-dom';

import CreateEditTaskSidebar from '@/ui/components/createEditTaskSidebar/CreateEditTaskSidebar';
import TaskReadCompleteDrawer from '@/ui/components/taskReadCompleteDrawer/TaskReadCompleteDrawer';

import { userHasRequiredPermission } from '@payaca/permissions/permissions.utils';
import { TasksPermissions } from '@payaca/permissions/tasks/tasks.permissions';
import { useDeal } from '@payaca/store/hooks/appState';
import useGetProjectByIdOverview from '../../../api/queries/project/useGetProjectByIdOverview';
import useGetProjectTasks from '../../../api/queries/project/useGetProjectTasks';
import { useSelector } from '../../../api/state';
import { GetTasksInput } from '../../../gql/graphql';
import {
  getTasksCompletionStatusesLocalStorageKey,
  getTasksEventRelationshipStatusesLocalStorageKey,
  getTasksTypesLocalStorageKey,
  getTasksUserAssignmentKeyLocalStorageKey,
} from '../../../helpers/localStorageKeyHelper';
import { getUserRoles } from '../../../utils/stateAccessors';
import ListedTasks, {
  ListedTasksPaginationInput,
  ListedTasksTTask,
} from '../listedTasks/ListedTasks';
import { PermissionGuard } from '../permissionGuard/PermissionGuard';

const tasksUserAssignmentKeyLocalStorageKey =
  getTasksUserAssignmentKeyLocalStorageKey();
const tasksCompletionStatusesLocalStorageKey =
  getTasksCompletionStatusesLocalStorageKey();
const tasksEventRelationshipStatusesLocalStorageKey =
  getTasksEventRelationshipStatusesLocalStorageKey();
const tasksTypesLocalStorageKey = getTasksTypesLocalStorageKey();

export const DealTasks: FC<{
  projectId: string;
  onDealUpdateSuccess: () => void;
}> = ({ projectId, onDealUpdateSuccess }) => {
  const deal = useDeal();
  const history = useHistory();
  const { path, url } = useRouteMatch();
  const userRoles = useSelector(getUserRoles);
  const { project } = useGetProjectByIdOverview(projectId);

  const isAddingOrEditingTask = useRouteMatch<{
    taskId: string;
  }>({
    path: `${path}/:taskId`,
    exact: true,
  });
  const initialUserAssignments = userHasRequiredPermission(userRoles, [
    TasksPermissions.GET_TASKS,
  ])
    ? localStorage
        .getItem(tasksUserAssignmentKeyLocalStorageKey)
        ?.split(',')
        .filter((x) => !!x) || []
    : [];
  const initialCompletionStatuses = localStorage
    .getItem(tasksCompletionStatusesLocalStorageKey)
    ?.split(',')
    .filter((x) => x != '') || ['to do', 'in progress'];
  const initialEventRelationshipStatuses =
    localStorage
      .getItem(tasksEventRelationshipStatusesLocalStorageKey)
      ?.split(',')
      .filter((x) => x != '') || [];
  const initialTypes = localStorage
    .getItem(tasksTypesLocalStorageKey)
    ?.split(',')
    .filter((x) => x != '');

  const [getTasksPagination, setGetTasksPagination] =
    useState<ListedTasksPaginationInput>({
      limit: 100,
      offset: 0,
    });
  const [getTasksInput, setGetTasksInput] = useState<GetTasksInput>({
    searchTerm: '',
    assignedTo: initialUserAssignments,
    completionStatuses: initialCompletionStatuses,
    eventRelationshipStatuses: initialEventRelationshipStatuses,
    sortBy: 'deadlineDate',
    sortDirection: 'asc',
    types: initialTypes,
  });

  const onUpdateInputData = (inputData: GetTasksInput) => {
    if ('assignedTo' in inputData) {
      localStorage.setItem(
        tasksUserAssignmentKeyLocalStorageKey,
        inputData.assignedTo?.join(',') || ''
      );
    }
    if ('completionStatuses' in inputData) {
      localStorage.setItem(
        tasksCompletionStatusesLocalStorageKey,
        inputData.completionStatuses?.join(',') || ''
      );
    }
    if ('eventRelationshipStatuses' in inputData) {
      localStorage.setItem(
        tasksEventRelationshipStatusesLocalStorageKey,
        inputData.eventRelationshipStatuses?.join(',') || ''
      );
    }
    if ('types' in inputData) {
      localStorage.setItem(
        tasksTypesLocalStorageKey,
        inputData.types?.join(',') || ''
      );
    }
    setGetTasksInput((x) => ({
      ...x,
      ...inputData,
    }));
  };
  const {
    data: tasksData,
    isLoading: isLoadingTasks,
    refetch: refetchTasks,
  } = useGetProjectTasks(projectId, getTasksInput, getTasksPagination);

  const [isTaskPersisted, setIsTaskPersisted] = useState(false);

  useEffect(() => {
    void refetchTasks();
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'refetchTasks'
  }, [getTasksInput, getTasksPagination]);

  const listedTasks = useMemo(() => {
    return tasksData?.projectById.tasks.items
      .map((task) => {
        const t = {
          id: task.id,
          name: task.name,
          deadlineDate: task.deadline,
          createdByUserId: task?.createdBy?.id,
          events:
            task?.events.map((event) => ({
              id: event.id,
              name: event.name,
            })) || [],
          assignee: task.assignee
            ? {
                id: task.assignee.id,
                firstName: task.assignee.firstName,
                lastName: task.assignee.lastName,
                email: task.assignee.email,
                colour: task.assignee.colour,
              }
            : undefined,
          completionStatus: task.completionStatus,
        };
        switch (task.__typename) {
          case 'ChecklistTask': {
            return {
              ...t,
              __typename: task.__typename,
            };
          }
          case 'FormTask': {
            return {
              ...t,
              __typename: task.__typename,
            };
          }
          case 'LegacyFormTask': {
            return {
              ...t,
              __typename: task.__typename,
            };
          }
          case 'MaterialsListTask': {
            return {
              ...t,
              __typename: task.__typename,
            };
          }
          case 'CustomFieldsTask': {
            return {
              ...t,
              __typename: task.__typename,
            };
          }
        }
      })
      .filter((t) => !!t) as ListedTasksTTask[];
  }, [tasksData?.projectById.tasks.items]);

  const headerContent = useMemo(() => {
    return {
      heading: 'Tasks',
      subHeading: '',
      buttons: (
        <>
          {!deal?.archivedAt && (
            <PermissionGuard
              renderIfHasPermissions={[
                TasksPermissions.PERSIST_SELF_ASSIGNED_DEAL_TASK,
                TasksPermissions.PERSIST_TASK,
              ]}
            >
              <AnchorButton as={Link} to={`${url}/new`} size={'sm'}>
                Create Task
              </AnchorButton>
            </PermissionGuard>
          )}
        </>
      ),
    };
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useMemo has a missing dependency: 'url'
  }, [deal?.archivedAt]);

  return (
    <div className="@container">
      <ListedTasks
        getTasksInput={getTasksInput}
        setGetTasksInput={onUpdateInputData}
        getTasksPagination={getTasksPagination}
        setGetTasksPagination={setGetTasksPagination}
        listedTasks={listedTasks}
        isLoadingTasks={isLoadingTasks}
        onPersistTaskSuccess={() => void refetchTasks()}
        hideProjectAndCustomerColumn
        totalCount={tasksData?.projectById.tasks.totalCount || 0}
        headerContent={headerContent}
      />
      <CreateEditTaskSidebar
        projectId={project?.id ? +project.id : undefined}
        isOpen={isAddingOrEditingTask?.params.taskId === 'new'}
        onClose={() => {
          history.push(url);
        }}
        onSuccess={() => {
          onDealUpdateSuccess();
        }}
      />
      <TaskReadCompleteDrawer
        isOpen={
          isAddingOrEditingTask
            ? isAddingOrEditingTask?.params.taskId !== 'new'
            : false
        }
        onClose={() => {
          history.push(url);
          if (isTaskPersisted) {
            void refetchTasks();
            onDealUpdateSuccess();
            setIsTaskPersisted(false);
          }
        }}
        taskId={
          isAddingOrEditingTask?.params.taskId
            ? parseInt(isAddingOrEditingTask.params.taskId)
            : undefined
        }
        onUpdateTaskSuccess={() => setIsTaskPersisted(true)}
        onDeleteTaskSuccess={() => {
          void refetchTasks();
          onDealUpdateSuccess();
        }}
      />
    </div>
  );
};
